import { cn } from '@/lib/utils'
import { useTurnContextOptional } from '../contexts'
import type { Player, TurnState } from '@/types/turn'

interface FilaDoTurnoProps {
  // Opcionais: sem elas, lê do contexto
  turnState?: TurnState | null
  players?: Player[]
  currentUserId?: string
}

/**
 * A fila da rodada, visível para todo mundo — uma linha só.
 *
 * A pergunta que o estudante fazia em voz alta no meio da aula era uma: "falta
 * quanto para a minha vez?". A primeira versão (fase 5) respondia mostrando a
 * ordem inteira numa fita de fichas que **rolava para o lado** — e no celular o
 * Paulo achou a rolagem lateral contra-intuitiva (2026-07-27), com razão:
 * rolagem escondida é convite a não ver metade do conteúdo.
 *
 * Agora a linha responde à pergunta em vez de exibir o dado bruto: quem joga
 * agora, quem vem depois, e a quantas vezes está a sua. Nunca cresce, nunca
 * rola, e não rouba altura do chat — que no celular é o que mais falta.
 *
 * **O que se perdeu, de propósito** (decisão do Paulo, ciente do custo): a
 * ordem inteira deixou de ficar à vista. Ela continua sendo definida — e
 * conferida — na tela de abrir rodada.
 */
export function FilaDoTurno(props: FilaDoTurnoProps) {
  const turnContext = useTurnContextOptional()

  const turnState = props.turnState !== undefined ? props.turnState : turnContext?.turnState
  const players = props.players ?? turnContext?.players ?? []

  if (!turnState?.isActive || turnState.playerOrder.length === 0) return null

  const fila = turnState.playerOrder
  const porId = new Map(players.map(p => [p.id, p]))

  const nomeDe = (id: string) => {
    if (props.currentUserId != null && id === props.currentUserId) return 'Você'
    const participante = porId.get(id)
    if (!participante) return '—'
    return participante.isNpc ? `${participante.name} (NPC)` : participante.name
  }

  // A vez atual pode não estar na fila num intervalo de reordenação; cair no
  // primeiro é melhor do que sumir com a linha inteira.
  const indiceAtual = Math.max(0, fila.indexOf(turnState.currentPlayerId ?? ''))
  const proximo = fila.length > 1 ? fila[(indiceAtual + 1) % fila.length] : null

  // Quantas vezes faltam até a minha. `null` = não estou na fila (o mestre, em
  // geral, e quem entrou depois da rodada aberta).
  const meuIndice = props.currentUserId ? fila.indexOf(props.currentUserId) : -1
  const minhaVezEm = meuIndice < 0
    ? null
    : (meuIndice - indiceAtual + fila.length) % fila.length

  // Só avisa o que a frase principal ainda não disse: com 0 ela já diz "é a sua
  // vez", e com 1 ela termina em "depois Você".
  const aviso = minhaVezEm === null
    ? `${fila.length} na fila`
    : minhaVezEm >= 2
      ? `sua vez em ${minhaVezEm}`
      : null

  return (
    <div
      aria-label="Ordem da rodada"
      aria-live="polite"
      className="flex items-center gap-2 px-2 py-1 text-xs min-w-0 border-b border-game-border-primary dark:border-white/[0.05] bg-game-secondary/50 dark:bg-zinc-900/50"
    >
      <span className="truncate min-w-0 text-muted-foreground">
        {minhaVezEm === 0 ? (
          <>É a <strong className="text-game-primary dark:text-zinc-100">sua vez</strong></>
        ) : (
          <>
            Agora{' '}
            <strong className="text-game-primary dark:text-zinc-100">
              {nomeDe(fila[indiceAtual])}
            </strong>
          </>
        )}
        {proximo && <> · depois {nomeDe(proximo)}</>}
      </span>

      {aviso && (
        <span
          className={cn(
            'ml-auto flex-shrink-0 px-2 py-0.5 rounded-full border tabular-nums',
            minhaVezEm === null
              ? 'text-muted-foreground border-border'
              : 'bg-primary/10 text-primary border-primary/30 font-medium',
          )}
        >
          {aviso}
        </span>
      )}
    </div>
  )
}
